import * as AgGrid from "ag-grid-community";
import * as notifications from "utils/notifications";
import ModalController from "../common/modal_controller";

interface GridDataWithActions {
  actions?: string;
}

export abstract class BaseRefundRequestModalController<T extends GridDataWithActions> extends ModalController {
  protected node: AgGrid.IRowNode<T> | null = null;
  protected gridApi: AgGrid.GridApi<T> | null = null;
  protected originalActions: string | null = null;
  protected requestCompleted: boolean = false;
  private escapeKeyHandler: ((event: KeyboardEvent) => void) | null = null;

  protected initializeModal(
    modalData: { node: AgGrid.IRowNode<T>; gridApi: AgGrid.GridApi<T>; originalActions: string } | undefined,
  ): void {
    if (modalData) {
      this.node = modalData.node;
      this.gridApi = modalData.gridApi;
      this.originalActions = modalData.originalActions;
    }
  }

  protected setupEscapeKeyHandler(): void {
    this.escapeKeyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && this.element.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();
        this.handleCancel();
      }
    };
    this.element.addEventListener("keydown", this.escapeKeyHandler);
  }

  protected setupFocusTrap(initialFocus: HTMLElement): void {
    const modalContent = this.element.querySelector(".modal-content") as HTMLElement;
    if (modalContent) {
      this.initializeFocusTrap(modalContent, {
        fallbackFocus: modalContent,
        initialFocus,
      });
    }
  }

  protected abstract handleCancel(): void;

  protected refreshGridCell(node: AgGrid.IRowNode<T>, columnName: string): void {
    if (this.gridApi && node.rowIndex !== null && node.rowIndex !== undefined) {
      try {
        this.gridApi.refreshCells({
          rowNodes: [node],
          columns: [columnName],
          force: true,
        });
      } catch (error) {
        // Ignore refresh errors
      }
    }
  }

  protected updateNodeActions(node: AgGrid.IRowNode<T>, originalActions: string): void {
    node.data.actions = originalActions;
    node.setDataValue("actions", originalActions);
    this.refreshGridCell(node, "actions");
  }

  protected restoreActions(): void {
    if (this.node && this.originalActions !== null && this.originalActions !== undefined) {
      this.updateNodeActions(this.node, this.originalActions);
    }
  }

  protected closeModal(): void {
    const turboFrame = document.querySelector("turbo-frame#modal") as HTMLElement;
    if (turboFrame) {
      turboFrame.innerHTML = "";
    }
  }

  protected handleError(errorMessage: string, onError?: () => void): void {
    notifications.alert(errorMessage);
    if (onError) {
      onError();
    }
    this.restoreActions();
    this.closeModal();
  }

  override disconnect(): void {
    super.disconnect();

    if (this.escapeKeyHandler) {
      this.element.removeEventListener("keydown", this.escapeKeyHandler);
      this.escapeKeyHandler = null;
    }

    if (!this.requestCompleted && this.node && this.originalActions && this.node.data.actions === "") {
      this.updateNodeActions(this.node, this.originalActions);
    }
  }
}

export default BaseRefundRequestModalController;
